<template>
  <div v-if="canArchiveProject && !isPersonalProject && !projectArchived" class="settings-block">
    <div class="section-content">
      <div class="form-row">
        <p class="muted" style="margin-bottom: 12px">
          {{
            s__(
              'ProjectSettings|Archiving a project will archive all tasks in the project and stop all notifications and reports. This action cannot be undone.'
            )
          }}
        </p>
        <button :disabled="archiving" class="btn-danger" @click="archiveProject">
          <span v-if="archiving">{{ s__('ProjectSettings|Archiving...') }}</span>
          <span v-else>{{ s__('ProjectSettings|Archive Project') }}</span>
        </button>
        <div v-if="archiveError" class="error" style="margin-top: 8px">{{ archiveError }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__ } from '@/locale'
import { showConfirm } from '@/utils/telegramWebApp'

const props = defineProps({
  projectId: String,
  canArchiveProject: Boolean,
  isPersonalProject: Boolean,
  projectArchived: Boolean,
})

const emit = defineEmits(['project-archived'])

const archiving = ref(false)
const archiveError = ref('')

const ARCHIVE_PROJECT = gql`
  mutation ArchiveProject($projectId: ID!) {
    archiveProject(projectId: $projectId) {
      project {
        id
        archived
      }
      errors
    }
  }
`

const { mutate: archiveProjectMutation } = useMutation(ARCHIVE_PROJECT)

async function archiveProject() {
  if (archiving.value) return

  const confirmed = await showConfirm(
    s__(
      'ProjectSettings|Are you sure you want to archive this project? All tasks will be archived and notifications will be disabled. This action cannot be undone.'
    )
  )
  if (!confirmed) return

  archiving.value = true
  archiveError.value = ''

  try {
    const { data } = await archiveProjectMutation({
      projectId: props.projectId,
    })

    if (data?.archiveProject?.errors?.length) {
      archiveError.value = data.archiveProject.errors.join(', ')
      archiving.value = false
      return
    }

    if (data?.archiveProject?.project) {
      // Emit event to parent to update state
      emit('project-archived', data.archiveProject.project)
    }
  } catch (err) {
    archiveError.value = err.message || s__('ProjectSettings|Failed to archive project')
  } finally {
    archiving.value = false
  }
}
</script>

<style scoped>
.form-row {
  margin-bottom: 16px;
}

.btn-danger {
  padding: 10px 24px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error {
  color: #dc3545;
  font-size: 14px;
  padding: 12px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
}

.muted {
  color: #6c757d;
  font-size: 14px;
}
</style>
